import * as React from "react";

import { headingTag, HeadingLevelProvider, useHeadingLevel, useRegisterTitle, useTitledLevel, type HeadingLevel } from "../lib/heading";
import { cn } from "../lib/utils";
import { Icon, type IconComponent } from "./icon";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "./resizable";
import { ScrollArea } from "./scroll-area";

/**
 * v2 PageLayout + Pane (redesign phase 5 finish, #455, break #151): the page
 * inside AppShell's `<main>`. PageHeader has no padding by design (04 PageHeader);
 * the gutters live here.
 *
 *   gutters   24 from 768 (AppShell's container query), 16 below and outside
 *             AppShell (the add-in). Header → content 24.
 *   width     `reading` caps the column at 768 (`max-w-3xl`): settings, forms,
 *             text. `full` for tables and workspaces. Left-aligned, so the
 *             header and the content share one gutter line.
 *   variant   `page` is the page's scroller (native, `scrollbar-gutter:
 *             stable`: switching to a taller tab doesn't shift the layout).
 *             `workspace` fills the height and never scrolls the page: the
 *             header sits above a row of panes, each pane scrolls itself
 *             (PaneBody = ScrollArea). With `aside`, the row is a Resizable
 *             group: main pane + a side panel (default 32 %, 24–50 %) behind
 *             a named handle.
 *   footer    `page` only: a sticky action bar at the bottom of the scroller,
 *             in the column (finding #10). For forms: render it while the form
 *             is dirty, with the hint ("Ungespeicherte Änderungen", a status)
 *             at the left and Verwerfen (outline) / Speichern (default) at the
 *             right; the buttons reach the form through `form={id}`.
 *
 * Pane / PaneHeader / PaneBody / PaneFooter: a pane with header and footer bands like the Card's
 * (48, padding 8 / 16, `--divider` rule), a title (Sora 14 Semibold, h2 by
 * default), an optional icon (16 `--ink-muted`; the AI marker on an AI pane:
 * "Assistent", "Quellen und Fakten" carry it once there, 04 Chat) and actions.
 * The pane is a `<section>` named by its title.
 *
 * One frame per region (finding #8): a pane is already a surface, so its whole
 * content is never wrapped in a Card. Title, count and search go into
 * PaneHeader, a table runs flush in `PaneBody padded={false}`, pagination
 * goes into PaneFooter. Several independent blocks in one pane (the ticket's
 * side panel) stay Cards, compact by default inside a Pane (`useInPane`).
 *
 * Headings (lib/heading): the page's h1 is the PageHeader in `header`; a page
 * without one (a workspace) passes `title`, rendered as a hidden h1. The
 * content starts at h2. A Pane's title sits at the context level (h2), and
 * everything inside the pane one deeper (a pane named by `aria-label` has no
 * title and adds no level).
 */

export type PageLayoutWidth = "reading" | "full";
export type PageLayoutVariant = "page" | "workspace";

export interface PageLayoutProps extends React.HTMLAttributes<HTMLDivElement> {
  /** A PageHeader (with filters or tabs below it, if any). */
  header?: React.ReactNode;
  /** The page's name as a hidden h1, for pages without a PageHeader (workspaces). */
  title?: string;
  width?: PageLayoutWidth;
  variant?: PageLayoutVariant;
  /** Workspace only: a side panel (a Pane) next to the main pane. */
  aside?: React.ReactNode;
  /** The resize handle's name. */
  asideLabel?: string;
  /** Percent of the row. Default 32. */
  asideDefaultSize?: number;
  /** `page` only: a sticky action bar below the content (a form's Verwerfen / Speichern). */
  footer?: React.ReactNode;
}

const GUTTER_X = "px-4 @3xl/shell:px-6";
const GUTTER_Y = "py-4 @3xl/shell:py-6";

const PageLayout = React.forwardRef<HTMLDivElement, PageLayoutProps>(function PageLayout(
  {
    header,
    title,
    width = "full",
    variant = "page",
    aside,
    asideLabel = "Breite des Detailbereichs ändern",
    asideDefaultSize = 32,
    footer,
    className,
    children,
    ...props
  },
  ref,
) {
  const cap = width === "reading" && "max-w-3xl";
  const hiddenTitle = !header && title ? <h1 className="sr-only">{title}</h1> : null;

  if (variant === "page") {
    return (
      <div
        ref={ref}
        data-slot="page-layout"
        className={cn("min-h-0 w-full flex-1 overflow-y-auto scrollbar-gutter-stable", GUTTER_X, GUTTER_Y, className)}
        {...props}
      >
        <div className={cn("flex w-full flex-col gap-6", cap)}>
          {hiddenTitle}
          {header}
          <HeadingLevelProvider level={2}>{children}</HeadingLevelProvider>
          {footer != null && (
            <div
              data-slot="page-footer"
              // Sticky stops at the scroller's padding: the negative offset (the bottom gutter) puts the bar on the edge.
              className="sticky -bottom-4 z-10 flex flex-wrap items-center justify-end gap-2 border-t border-divider bg-background py-3 @3xl/shell:-bottom-6"
            >
              {footer}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} data-slot="page-layout" data-variant="workspace" className={cn("flex min-h-0 w-full flex-1 flex-col", className)} {...props}>
      {hiddenTitle}
      {header && <div className={cn("shrink-0 pt-4 pb-4 @3xl/shell:pt-6", GUTTER_X)}>{header}</div>}
      <HeadingLevelProvider level={2}>
        <div className={cn("flex min-h-0 flex-1", header && "border-t border-divider")}>
          {aside ? (
            <ResizablePanelGroup direction="horizontal">
              <ResizablePanel defaultSize={100 - asideDefaultSize} minSize={40} className="flex min-w-0 flex-col">
                {children}
              </ResizablePanel>
              <ResizableHandle aria-label={asideLabel} />
              <ResizablePanel defaultSize={asideDefaultSize} minSize={24} maxSize={50} className="flex min-w-0 flex-col">
                {aside}
              </ResizablePanel>
            </ResizablePanelGroup>
          ) : (
            <div className="flex min-w-0 flex-1 flex-col">{children}</div>
          )}
        </div>
      </HeadingLevelProvider>
    </div>
  );
});

const PaneContext = React.createContext<{ titleId: string; level: HeadingLevel; register: () => () => void } | undefined>(
  undefined,
);

export type PaneProps = React.HTMLAttributes<HTMLElement>;

/** True inside a Pane: Cards default to compact, AiAnswerBlock to `sm` actions. */
export function useInPane() {
  return React.useContext(PaneContext) !== undefined;
}

/** A `<section>` named by its PaneHeader (or by `aria-label` when it has none). Fills its slot; the body scrolls. */
const Pane = React.forwardRef<HTMLElement, PaneProps>(function Pane({ className, children, ...props }, ref) {
  const titleId = React.useId();
  const level = useHeadingLevel(undefined, 2);
  const { contentLevel, register } = useTitledLevel(level);
  const context = React.useMemo(() => ({ titleId, level, register }), [titleId, level, register]);
  return (
    <PaneContext.Provider value={context}>
      <section
        ref={ref}
        aria-labelledby={props["aria-label"] ? undefined : titleId}
        data-slot="pane"
        className={cn("flex h-full min-h-0 min-w-0 flex-1 flex-col bg-background", className)}
        {...props}
      >
        <HeadingLevelProvider level={contentLevel}>{children}</HeadingLevelProvider>
      </section>
    </PaneContext.Provider>
  );
});

export interface PaneHeaderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  title: React.ReactNode;
  /** Wins over the pane's context level (h2 in a page). */
  headingLevel?: HeadingLevel;
  /** 16, `--ink-muted`; the AiMarker keeps its gradient. */
  icon?: IconComponent;
  actions?: React.ReactNode;
}

function PaneHeader({ title, headingLevel, icon, actions, className, children, ...props }: PaneHeaderProps) {
  const pane = React.useContext(PaneContext);
  const titleId = pane?.titleId;
  useRegisterTitle(pane?.register);
  const Heading = headingTag(headingLevel ?? pane?.level ?? 2);
  return (
    <div
      data-slot="pane-header"
      className={cn("flex min-h-12 shrink-0 flex-wrap items-center gap-2 border-b border-divider px-4 py-2", className)}
      {...props}
    >
      {/* `basis-40`: when narrow the actions wrap under the title instead of cutting it (the CardHeader rule). */}
      <div className="flex min-w-0 grow basis-40 items-center gap-2">
        {icon && <Icon icon={icon} className="text-ink-muted" />}
        <Heading id={titleId} className="min-w-0 truncate font-sans text-label text-ink">
          {title}
        </Heading>
        {children}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-1">{actions}</div>}
    </div>
  );
}

export interface PaneBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Default true: padding 16. */
  padded?: boolean;
  /** For read-only content: the viewport becomes a focusable, named region. */
  viewportLabel?: string;
}

function PaneBody({ padded = true, viewportLabel, className, children, ...props }: PaneBodyProps) {
  return (
    <ScrollArea data-slot="pane-body" viewportLabel={viewportLabel} className="min-h-0 flex-1">
      <div className={cn("flex flex-col gap-3", padded && "p-4", className)} {...props}>
        {children}
      </div>
    </ScrollArea>
  );
}

/** The pane's footer band (the CardFooter band): pagination, the selection count. Actions right; give leading meta `mr-auto`. */
function PaneFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="pane-footer"
      className={cn("flex min-h-12 shrink-0 items-center justify-end gap-2 border-t border-divider px-4 py-2", className)}
      {...props}
    />
  );
}

export { PageLayout, Pane, PaneHeader, PaneBody, PaneFooter };
